import { ReactNode } from 'react';
import { LegalLayout, LegalSection, useIsJa } from '@/components/layout/LegalLayout';
import { Link } from 'react-router-dom';

interface LegalCopy {
  h: string;
  body: ReactNode;
}

interface LegalSectionDef {
  id: string;
  en: LegalCopy;
  ja: LegalCopy;
}

const SECTIONS: LegalSectionDef[] = [
  {
    id: 'intro',
    en: {
      h: '0. Introduction',
      body: (
        <p>
          This Data Processing Agreement ("DPA") forms part of the{' '}
          <Link to="/terms">Terms of Service</Link> between CadencEZ Inc., a Delaware (USA)
          corporation ("Processor"), and the Customer ("Controller") and applies where CadencEZ
          processes personal data on the Controller's behalf in providing the Service. It is
          designed to satisfy Article 28 of the GDPR and equivalent requirements under the UK GDPR
          and Japan's Act on the Protection of Personal Information ("APPI"). The governing law of
          the Terms is Delaware, USA. Customers needing a signed copy can request one at{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>.
        </p>
      ),
    },
    ja: {
      h: '0. はじめに',
      body: (
        <p>
          本データ処理契約（以下「本DPA」といいます）は、デラウェア州（米国）法人である CadencEZ
          Inc.（以下「処理者」（委託先）といいます）とお客様（以下「管理者」といいます）との間の
          <Link to="/terms">利用規約</Link>
          の一部を構成し、CadencEZ が本サービスの提供にあたり管理者に代わって個人情報（個人データ）を
          取扱う場合に適用されます。本DPAは、GDPR 第28条、ならびに英国GDPRおよび日本の個人情報保護法
          （以下「APPI」といいます）の同等の要件を満たすよう設計されています。本利用規約の準拠法は
          米国デラウェア州法とします。署名済みの写しが必要なお客様は{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a> までご請求ください。
        </p>
      ),
    },
  },
  {
    id: 'roles',
    en: {
      h: '1. Roles & scope',
      body: (
        <p>
          The Controller determines the purposes and means of processing the personal data
          contained in Customer Data. The Processor processes that personal data only on documented
          instructions from the Controller, namely to provide the Service and as described in the
          Terms and Privacy Policy.
        </p>
      ),
    },
    ja: {
      h: '1. 役割および範囲',
      body: (
        <p>
          管理者は、お客様データに含まれる個人情報の取扱いの目的および方法を決定します。処理者は、
          当該個人情報を管理者の文書化された指示（すなわち本サービスを提供するため、ならびに利用規約
          およびプライバシーポリシーに記載のとおり）に基づいてのみ取扱います。
        </p>
      ),
    },
  },
  {
    id: 'subject',
    en: {
      h: '2. Subject matter, nature & duration',
      body: (
        <p>
          Processing covers the personal data the Controller submits to the Service (for example,
          names and business emails of the Controller's users, and any personal data within uploaded
          financial or headcount datasets), for the duration of the subscription plus any limited
          retention period described in the Privacy Policy. Further detail appears in{' '}
          <a href="#annex-1">Annex I</a>.
        </p>
      ),
    },
    ja: {
      h: '2. 取扱いの対象・性質・期間',
      body: (
        <p>
          取扱いの対象は、管理者が本サービスに提供する個人情報（たとえば管理者の利用者の氏名・業務用
          メールアドレス、ならびにアップロードされた財務データや人員データに含まれる個人情報）であり、
          その期間は契約期間およびプライバシーポリシーに記載の限定的な保存期間とします。詳細は
          <a href="#annex-1">別紙I</a>に記載します。
        </p>
      ),
    },
  },
  {
    id: 'obligations',
    en: {
      h: '3. Processor obligations',
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Process personal data only on the Controller's documented instructions.</li>
          <li>Ensure personnel authorized to process data are bound by confidentiality.</li>
          <li>Implement appropriate technical and organizational measures (Section 5 and <a href="#annex-2">Annex II</a>).</li>
          <li>Assist the Controller with data-subject requests and with security, breach-notification, and impact-assessment obligations.</li>
          <li>Make available information needed to demonstrate compliance.</li>
        </ul>
      ),
    },
    ja: {
      h: '3. 処理者の義務',
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>個人情報を管理者の文書化された指示に基づいてのみ取扱うこと。</li>
          <li>個人情報の取扱いを許可された従業者が秘密保持義務を負うことを確保すること。</li>
          <li>適切な技術的・組織的措置を実施すること（第5条および<a href="#annex-2">別紙II</a>）。</li>
          <li>データ主体（本人）からの請求への対応、ならびに安全管理・漏えい通知・影響評価に関する義務について管理者を支援すること。</li>
          <li>コンプライアンスを実証するために必要な情報を提供すること。</li>
        </ul>
      ),
    },
  },
  {
    id: 'subprocessors',
    en: {
      h: '4. Sub-processors',
      body: (
        <>
          <p>
            The Controller grants the Processor general authorization to engage the sub-processors
            listed on the <Link to="/security">Security &amp; sub-processors</Link> page and in{' '}
            <a href="#annex-3">Annex III</a>. The Processor imposes data-protection obligations on
            each sub-processor no less protective than this DPA and remains responsible for their
            performance.
          </p>
          <p>
            <strong>Change procedure.</strong> Before adding or replacing a sub-processor, the
            Processor will give the Controller at least 30 days' advance notice, either by updating
            the <Link to="/security">/security</Link> page or by email. The Controller may object to
            a new sub-processor on reasonable data-protection grounds within that notice period. The
            parties will work in good faith to resolve the objection; if it cannot be resolved, the
            Controller may terminate the affected Service.
          </p>
        </>
      ),
    },
    ja: {
      h: '4. 再委託先',
      body: (
        <>
          <p>
            管理者は、<Link to="/security">セキュリティおよび再委託先</Link>のページならびに
            <a href="#annex-3">別紙III</a>に記載された再委託先を起用することについて、処理者に包括的
            な承認を与えます。処理者は、各再委託先に対し本DPAと同等以上の保護水準を有するデータ保護
            義務を課し、その履行について引き続き責任を負います。
          </p>
          <p>
            <strong>変更手続。</strong>処理者は、再委託先を追加または変更する前に、
            <Link to="/security">/security</Link> ページの更新または電子メールにより、少なくとも30日前
            までに管理者へ事前通知します。管理者は、当該通知期間内に、合理的なデータ保護上の理由に
            基づき新たな再委託先に異議を述べることができます。両当事者は誠実に協議して異議の解決に
            努めるものとし、解決に至らない場合、管理者は当該影響を受ける本サービスを解約することが
            できます。
          </p>
        </>
      ),
    },
  },
  {
    id: 'security',
    en: {
      h: '5. Security measures',
      body: (
        <p>
          The Processor maintains measures including: encryption in transit (TLS) and encryption at
          rest for sensitive secrets (AES-256-GCM), encrypted backups, role-based access control
          (RBAC), per-workspace logical isolation, available multi-factor authentication (MFA),
          immutable audit logging of administrative and security events, least-privilege access for
          staff, and regular tested backups. The full set of technical and organizational measures
          is set out in <a href="#annex-2">Annex II</a>; further detail is on the{' '}
          <Link to="/security">Security</Link> page.
        </p>
      ),
    },
    ja: {
      h: '5. 安全管理措置',
      body: (
        <p>
          処理者は、次の措置を含む対策を維持します。すなわち、通信経路上の暗号化（TLS）および機微な
          秘密情報の保存時の暗号化（AES-256-GCM）、暗号化されたバックアップ、役割ベースのアクセス制御
          （RBAC）、ワークスペースごとの論理的分離、多要素認証（MFA）の利用、管理・セキュリティ事象の
          改ざん不能な監査ログ、従業者に対する最小権限の付与、ならびに定期的に復元テストを行う
          バックアップです。技術的・組織的措置の全容は<a href="#annex-2">別紙II</a>に記載します。
          詳細は<Link to="/security">セキュリティ</Link>ページをご参照ください。
        </p>
      ),
    },
  },
  {
    id: 'transfers',
    en: {
      h: '6. International transfers',
      body: (
        <>
          <p>
            Customer Data is hosted in the European Union (Germany, via Hetzner). Where providing the
            Service requires a transfer of personal data outside the originating region, the
            Processor relies on a lawful transfer mechanism, including:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>For transfers of EU/EEA personal data: the EU Standard Contractual Clauses (SCCs), <strong>Module Two (controller-to-processor)</strong>.</li>
            <li>For transfers of UK personal data: the UK International Data Transfer Agreement (IDTA) or the UK Addendum to the EU SCCs.</li>
            <li>For transfers of Swiss personal data: the Swiss addendum to the SCCs, where applicable.</li>
            <li>For personal data subject to Japan's APPI: appropriate safeguards and, where required, the data subject's consent for cross-border provision.</li>
          </ul>
        </>
      ),
    },
    ja: {
      h: '6. 越境移転',
      body: (
        <>
          <p>
            お客様データは欧州連合（ドイツ、Hetzner 経由）でホスティングされます。本サービスの提供に
            あたり、生成元地域の外への個人情報の移転が必要となる場合、処理者は次を含む適法な移転手段に
            依拠します。
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>EU/EEA の個人情報の移転については、EU標準契約条項（SCC）の<strong>モジュール2（管理者から処理者へ）</strong>。</li>
            <li>英国の個人情報の移転については、英国国際データ移転協定（IDTA）またはEU SCCに対する英国補遺。</li>
            <li>スイスの個人情報の移転については、該当する場合、SCCに対するスイス補遺。</li>
            <li>日本のAPPIの適用を受ける個人情報については、適切な安全管理措置、および必要に応じて越境提供に係る本人の同意。</li>
          </ul>
        </>
      ),
    },
  },
  {
    id: 'breach',
    en: {
      h: '7. Personal-data breaches',
      body: (
        <p>
          The Processor will notify the Controller without undue delay after becoming aware of a
          personal-data breach affecting Customer Data, and provide information reasonably needed for
          the Controller to meet its own notification duties.
        </p>
      ),
    },
    ja: {
      h: '7. 個人データの漏えい等',
      body: (
        <p>
          処理者は、お客様データに影響する個人データの漏えい等を認識した後、不当に遅滞することなく
          管理者に通知し、管理者が自らの通知義務を果たすために合理的に必要な情報を提供します。
        </p>
      ),
    },
  },
  {
    id: 'dsr',
    en: {
      h: '8. Data-subject requests',
      body: (
        <p>
          Taking into account the nature of processing, the Processor assists the Controller by
          appropriate measures to respond to requests to exercise data-subject rights (access,
          rectification, erasure, portability, restriction, objection).
        </p>
      ),
    },
    ja: {
      h: '8. データ主体（本人）からの請求',
      body: (
        <p>
          処理者は、取扱いの性質を考慮し、データ主体（本人）の権利（開示、訂正、消去、データポータ
          ビリティ、利用の制限、異議）の行使に係る請求への対応について、適切な措置により管理者を
          支援します。
        </p>
      ),
    },
  },
  {
    id: 'deletion',
    en: {
      h: '9. Return & deletion',
      body: (
        <p>
          On termination, and on the Controller's request, the Processor will delete or return
          Customer Data, except where retention is required by law. Self-service export is available
          before deletion.
        </p>
      ),
    },
    ja: {
      h: '9. 返却および消去',
      body: (
        <p>
          契約終了時、かつ管理者の要請があった場合、処理者は、法令により保存が求められる場合を除き、
          お客様データを消去または返却します。消去前にはセルフサービスによるエクスポートが利用可能
          です。
        </p>
      ),
    },
  },
  {
    id: 'audits',
    en: {
      h: '10. Audits',
      body: (
        <p>
          The Processor will make available information necessary to demonstrate compliance with this
          DPA and allow for and contribute to audits, including inspections, conducted by the
          Controller or an auditor it mandates, subject to reasonable confidentiality and scheduling.
        </p>
      ),
    },
    ja: {
      h: '10. 監査',
      body: (
        <p>
          処理者は、本DPAの遵守を実証するために必要な情報を提供し、管理者またはその委任する監査人が
          実施する監査（立入検査を含む）に応じ、これに協力します。ただし、合理的な秘密保持および日程
          調整に従うものとします。
        </p>
      ),
    },
  },
  {
    id: 'annex-1',
    en: {
      h: 'Annex I — Details of processing',
      body: (
        <div className="annex">
          <h3 className="text-base font-semibold text-ink-900">A. List of parties &amp; roles</h3>
          <p>
            Controller: the Customer (the entity that subscribes to the Service). Processor:
            CadencEZ Inc., a Delaware (USA) corporation. The Controller acts as controller, and the
            Processor as processor, in respect of the personal data within Customer Data.
          </p>
          <h3 className="text-base font-semibold text-ink-900">B. Categories of data subjects</h3>
          <p>
            The Controller's authorized users and employees; and individuals appearing in the
            Controller's uploaded financial, payroll, and headcount data.
          </p>
          <h3 className="text-base font-semibold text-ink-900">C. Categories of personal data</h3>
          <p>
            Names, business email addresses, job titles, and departments; and personal data within
            uploaded datasets, such as salary, compensation, and headcount figures linked to
            identifiable individuals. <strong>Special-category data:</strong> none is requested or
            intended to be processed.
          </p>
          <h3 className="text-base font-semibold text-ink-900">D. Nature &amp; purpose of processing</h3>
          <p>
            Hosting, storage, computation, multi-entity consolidation, and AI-assisted analysis, in
            each case to provide the Service to the Controller.
          </p>
          <h3 className="text-base font-semibold text-ink-900">E. Duration of processing</h3>
          <p>
            The duration of the subscription term, plus a limited post-termination retention period
            as described in the Privacy Policy and Section 9.
          </p>
          <h3 className="text-base font-semibold text-ink-900">F. Frequency of processing</h3>
          <p>Continuous, for so long as the Controller uses the Service.</p>
        </div>
      ),
    },
    ja: {
      h: '別紙I — 取扱いの詳細',
      body: (
        <div className="annex">
          <h3 className="text-base font-semibold text-ink-900">A. 当事者および役割の一覧</h3>
          <p>
            管理者：お客様（本サービスを契約する主体）。処理者：デラウェア州（米国）法人 CadencEZ
            Inc.。お客様データに含まれる個人情報に関し、管理者は管理者として、処理者は処理者（委託先）
            として行為します。
          </p>
          <h3 className="text-base font-semibold text-ink-900">B. データ主体（本人）の類型</h3>
          <p>
            管理者の許可された利用者および従業員、ならびに管理者がアップロードする財務・給与・人員
            データに登場する個人。
          </p>
          <h3 className="text-base font-semibold text-ink-900">C. 個人情報（個人データ）の類型</h3>
          <p>
            氏名、業務用メールアドレス、役職、部署、ならびにアップロードされたデータセットに含まれる
            個人情報（特定の個人に紐づく給与・報酬・人員数等の数値）。<strong>要配慮個人情報（特別
            な種類のデータ）：</strong>取扱いを要求せず、また意図しません。
          </p>
          <h3 className="text-base font-semibold text-ink-900">D. 取扱いの性質および目的</h3>
          <p>
            ホスティング、保存、計算処理、複数法人の連結、およびAIを活用した分析であり、いずれも
            管理者に本サービスを提供するために行います。
          </p>
          <h3 className="text-base font-semibold text-ink-900">E. 取扱いの期間</h3>
          <p>
            契約期間、ならびにプライバシーポリシーおよび第9条に記載の限定的な契約終了後の保存期間。
          </p>
          <h3 className="text-base font-semibold text-ink-900">F. 取扱いの頻度</h3>
          <p>継続的（管理者が本サービスを利用する限り）。</p>
        </div>
      ),
    },
  },
  {
    id: 'annex-2',
    en: {
      h: 'Annex II — Technical & organizational measures (Art. 32)',
      body: (
        <div className="annex">
          <p>The Processor implements and maintains the following measures:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Encryption</strong> — TLS for data in transit; AES-256-GCM for sensitive secrets at rest; encrypted backups.</li>
            <li><strong>Access control</strong> — role-based access control (RBAC), per-workspace logical isolation, multi-factor authentication (MFA) available, and least-privilege access to production systems.</li>
            <li><strong>Logging &amp; monitoring</strong> — immutable audit log of administrative and security events (actor, timestamp, source).</li>
            <li><strong>Resilience</strong> — regular, tested encrypted backups.</li>
            <li><strong>Vulnerability &amp; patch management</strong> — ongoing patching and remediation of identified vulnerabilities.</li>
            <li><strong>Personnel</strong> — confidentiality obligations and security-awareness practices for staff with access.</li>
            <li><strong>Sub-processor management</strong> — each sub-processor bound by equivalent data-protection obligations.</li>
            <li><strong>Physical security</strong> — delegated to Hetzner's EU data centres.</li>
            <li><strong>Incident response &amp; breach notification</strong> — defined process for detecting, responding to, and notifying personal-data breaches (Section 7).</li>
          </ul>
        </div>
      ),
    },
    ja: {
      h: '別紙II — 技術的・組織的措置（第32条）',
      body: (
        <div className="annex">
          <p>処理者は、次の措置を実施し、維持します。</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>暗号化</strong> — 通信経路上のデータにはTLS、保存時の機微な秘密情報にはAES-256-GCM、ならびに暗号化されたバックアップ。</li>
            <li><strong>アクセス制御</strong> — 役割ベースのアクセス制御（RBAC）、ワークスペースごとの論理的分離、多要素認証（MFA）の利用、ならびに本番システムへの最小権限アクセス。</li>
            <li><strong>ログおよび監視</strong> — 管理・セキュリティ事象の改ざん不能な監査ログ（実行者、時刻、発信元）。</li>
            <li><strong>レジリエンス</strong> — 定期的に復元テストを行う暗号化されたバックアップ。</li>
            <li><strong>脆弱性およびパッチ管理</strong> — 継続的なパッチ適用および特定された脆弱性の是正。</li>
            <li><strong>人的措置</strong> — アクセス権を有する従業者に対する秘密保持義務およびセキュリティ意識向上の取組み。</li>
            <li><strong>再委託先の管理</strong> — 各再委託先は同等のデータ保護義務を負う。</li>
            <li><strong>物理的セキュリティ</strong> — Hetzner のEU内データセンターに委ねる。</li>
            <li><strong>インシデント対応および漏えい通知</strong> — 個人データの漏えい等の検知・対応・通知に係る定められた手続（第7条）。</li>
          </ul>
        </div>
      ),
    },
  },
  {
    id: 'annex-3',
    en: {
      h: 'Annex III — Sub-processors',
      body: (
        <div className="annex">
          <p>
            The Processor engages the following sub-processors to operate the Service. The canonical,
            up-to-date list is maintained on the <Link to="/security">Security &amp; sub-processors</Link>{' '}
            page.
          </p>
          <table>
            <thead>
              <tr>
                <th>Sub-processor</th>
                <th>Purpose</th>
                <th>Region</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Hetzner Online GmbH</td><td>Cloud hosting &amp; compute</td><td>Germany (EU)</td></tr>
              <tr><td>Cloudflare, Inc. (R2)</td><td>Object storage (uploads, exports, backups)</td><td>EU / global edge</td></tr>
              <tr><td>Stripe, Inc.</td><td>Payments &amp; subscription billing</td><td>US / EU</td></tr>
              <tr><td>Amazon Web Services (SES)</td><td>Transactional email</td><td>EU</td></tr>
              <tr><td>OpenRouter, Inc.</td><td>AI model gateway (CadencEZ AI)</td><td>US</td></tr>
              <tr><td>Google LLC</td><td>Optional Google sign-in (SSO)</td><td>US / global</td></tr>
            </tbody>
          </table>
        </div>
      ),
    },
    ja: {
      h: '別紙III — 再委託先',
      body: (
        <div className="annex">
          <p>
            処理者は、本サービスの運用のため次の再委託先を起用します。最新の正式な一覧は
            <Link to="/security">セキュリティおよび再委託先</Link>のページで管理されます。
          </p>
          <table>
            <thead>
              <tr>
                <th>再委託先</th>
                <th>目的</th>
                <th>地域</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Hetzner Online GmbH</td><td>クラウドホスティングおよび計算処理</td><td>ドイツ（EU）</td></tr>
              <tr><td>Cloudflare, Inc. (R2)</td><td>オブジェクトストレージ（アップロード・エクスポート・バックアップ）</td><td>EU／グローバルエッジ</td></tr>
              <tr><td>Stripe, Inc.</td><td>決済およびサブスクリプション課金</td><td>米国／EU</td></tr>
              <tr><td>Amazon Web Services (SES)</td><td>トランザクションメール</td><td>EU</td></tr>
              <tr><td>OpenRouter, Inc.</td><td>AIモデルゲートウェイ（CadencEZ AI）</td><td>米国</td></tr>
              <tr><td>Google LLC</td><td>任意のGoogleサインイン（SSO）</td><td>米国／グローバル</td></tr>
            </tbody>
          </table>
        </div>
      ),
    },
  },
];

export function DpaPage() {
  const isJa = useIsJa();
  return (
    <LegalLayout
      title={isJa ? 'データ処理契約（DPA）' : 'Data Processing Agreement'}
      updated="2026-06-03"
      metaTitle={
        isJa
          ? 'データ処理契約（DPA） — CadencEZ'
          : 'Data Processing Agreement (DPA) — CadencEZ'
      }
      metaDescription={
        isJa
          ? 'CadencEZのデータ処理契約。役割、安全管理措置、再委託先、越境移転、データ主体の権利について、GDPRおよびAPPIに基づき説明します。'
          : "CadencEZ's Data Processing Agreement describing roles, security measures, sub-processors, international transfers, and data-subject rights under GDPR and APPI."
      }
      path="/dpa"
    >
      {SECTIONS.map((s) => {
        const c = isJa ? s.ja : s.en;
        return (
          <div id={s.id} key={s.id}>
            <LegalSection heading={c.h}>{c.body}</LegalSection>
          </div>
        );
      })}
    </LegalLayout>
  );
}
